/**
 * Друкована форма «Довідка-розрахунок: Переоцінка активів і зобовʼязань,
 * виражених в іноземній валюті» — формат як у 1С (скрін власника 2026-08-23):
 * рахунок · аналітика · валюта · сума у валюті · сума в грн до · курс ·
 * сума в грн після · сума переоцінки · доходи · витрати + Разом.
 */
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { fxRevaluations } from '@/api/fxRevaluations';
import { orgs } from '@/api/catalogs';
import PrintActions from '@/shared/components/PrintActions';

interface PrintRow {
  revaluation_date?: string | null;
  line_number: number;
  account_code: string;
  analytics: string | null;
  currency_code: string;
  currency_balance: string;
  old_uah_balance: string;
  new_rate: string;
  new_uah_balance: string;
  fx_diff: string;
  entry_kind: string;
}

const fmt = (v: string | number | null | undefined, digits = 2) =>
  Number(v ?? 0).toLocaleString('uk-UA', { minimumFractionDigits: digits, maximumFractionDigits: digits });

const cell: React.CSSProperties = {
  border: '1px solid #000', padding: '3pt 5pt', verticalAlign: 'top', fontSize: '9pt',
};
const num: React.CSSProperties = { ...cell, textAlign: 'right' };

const MONTHS = ['', 'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];

export default function FxRevaluationPrintPage() {
  const { id } = useParams();
  const docQ = useQuery({
    queryKey: ['fx-revaluation', id],
    queryFn: () => fxRevaluations.get(id!),
    enabled: !!id,
  });
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const rowsQ = useQuery({
    queryKey: ['fx-revaluation-print-rows', id],
    queryFn: () => api.get<{ items: PrintRow[] }>(`/fx-revaluations/${id}/print-rows`).then((r) => r.data),
    enabled: !!id,
  });

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const doc = docQ.data;
  const org = orgsQ.data?.items.find((o) => o.id === doc?.organization_id);
  if (!doc || !org) return <div style={{ padding: '2rem' }}>Завантаження…</div>;
  const rows = rowsQ.data?.items ?? [];
  // Помісячна розбивка (0441): групуємо рядки за revaluation_date.
  const months = rows.reduce((m, r) => {
    const k = (r.revaluation_date ?? doc.revaluation_date).slice(0, 10);
    (m.get(k) ?? m.set(k, []).get(k)!).push(r);
    return m;
  }, new Map<string, PrintRow[]>());
  const monthKeys = Array.from(months.keys()).sort();
  const rd = new Date(doc.revaluation_date);

  return (
    <div className="print-page" style={{
      padding: '12mm', fontFamily: 'Times, serif', fontSize: '10.5pt',
      lineHeight: 1.35, maxWidth: '290mm', margin: '0 auto', background: 'var(--bg-panel)',
    }}>
      <PrintActions docMeta={{
        organizationId: doc.organization_id,
        documentType: 'doc.fx_revaluations',
        documentId: doc.id,
        filename: `Довідка-розрахунок переоцінки валюти ${doc.document_number}`,
        orgName: org.name,
      }} />

      <div style={{ marginBottom: '0.5rem' }}>
        Організація: <strong>{org.full_name || org.name}</strong>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.8rem' }}>
        <h2 style={{ margin: 0, fontSize: '14pt' }}>Довідка-розрахунок</h2>
        <table style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <td style={{ ...cell, fontWeight: 700, textAlign: 'center', width: 90 }}>Номер</td>
              <td style={{ ...cell, fontWeight: 700, textAlign: 'center', width: 100 }}>Дата</td>
              <td style={{ ...cell, fontWeight: 700, textAlign: 'center', width: 120 }}>Період</td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ ...cell, textAlign: 'center' }}>{doc.document_number}</td>
              <td style={{ ...cell, textAlign: 'center' }}>{rd.toLocaleDateString('uk-UA')}</td>
              <td style={{ ...cell, textAlign: 'center' }}>
                {doc.period_start && doc.period_start.slice(0, 7) !== doc.revaluation_date.slice(0, 7)
                  ? `${MONTHS[Number(doc.period_start.slice(5, 7))]} ${doc.period_start.slice(0, 4)} — ${MONTHS[rd.getMonth() + 1]} ${rd.getFullYear()} р.`
                  : `${MONTHS[rd.getMonth() + 1]} ${rd.getFullYear()} р.`}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3 style={{ margin: '0 0 0.5rem', fontSize: '11.5pt' }}>
        Переоцінка активів і зобовʼязань, виражених в іноземній валюті
      </h3>

      {monthKeys.map((mk) => {
        const mrows = months.get(mk)!;
        const md = new Date(mk);
        const mt = mrows.reduce((t, r) => {
          const d = Number(r.fx_diff);
          t.old += Number(r.old_uah_balance); t.neu += Number(r.new_uah_balance); t.diff += d;
          if (d > 0) t.income += d; else t.expense += -d;
          return t;
        }, { old: 0, neu: 0, diff: 0, income: 0, expense: 0 });
        return (
      <div key={mk} style={{ marginBottom: '1rem' }}>
        {monthKeys.length > 1 && (
          <div style={{ fontWeight: 700, margin: '0.6rem 0 0.3rem' }}>
            {MONTHS[md.getMonth() + 1]} {md.getFullYear()} р. — на {md.toLocaleDateString('uk-UA')}
          </div>
        )}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ fontWeight: 700, textAlign: 'center' }}>
            <td style={{ ...cell, width: '6%' }} rowSpan={2}>Рахунок обліку</td>
            <td style={{ ...cell, width: '20%' }} rowSpan={2}>Аналітика</td>
            <td style={{ ...cell, width: '6%' }} rowSpan={2}>Валюта</td>
            <td style={{ ...cell, width: '10%' }} rowSpan={2}>Сума у валюті на дату переоцінки</td>
            <td style={{ ...cell, width: '11%' }} rowSpan={2}>Сума в гривнях на дату переоцінки</td>
            <td style={{ ...cell, width: '8%' }} rowSpan={2}>Курс на дату переоцінки</td>
            <td style={{ ...cell, width: '11%' }} rowSpan={2}>Сума в гривнях після переоцінки (гр.4 × гр.6)</td>
            <td style={{ ...cell }} colSpan={3}>Результати переоцінки (курсові різниці)</td>
          </tr>
          <tr style={{ fontWeight: 700, textAlign: 'center' }}>
            <td style={{ ...cell, width: '10%' }}>Сума переоцінки, грн (гр.7 − гр.5)</td>
            <td style={{ ...cell, width: '9%' }}>Сума доходів</td>
            <td style={{ ...cell, width: '9%' }}>Сума витрат</td>
          </tr>
          <tr style={{ textAlign: 'center', fontSize: '8pt', color: '#333' }}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => <td key={n} style={{ ...cell, padding: '1pt' }}>{n}</td>)}
          </tr>
        </thead>
        <tbody>
          {mrows.map((r) => {
            const d = Number(r.fx_diff);
            return (
              <tr key={r.line_number}>
                <td style={{ ...cell, textAlign: 'center' }}>{r.account_code}</td>
                <td style={cell}>{r.analytics ?? ''}</td>
                <td style={{ ...cell, textAlign: 'center' }}>{r.currency_code}</td>
                <td style={num}>{fmt(r.currency_balance)}</td>
                <td style={num}>{fmt(r.old_uah_balance)}</td>
                <td style={num}>{fmt(r.new_rate, 4)}</td>
                <td style={num}>{fmt(r.new_uah_balance)}</td>
                <td style={num}>{d < 0 ? '−' : ''}{fmt(Math.abs(d))}</td>
                <td style={num}>{d > 0 ? fmt(d) : ''}</td>
                <td style={num}>{d < 0 ? fmt(-d) : ''}</td>
              </tr>
            );
          })}
          <tr style={{ fontWeight: 700 }}>
            <td style={cell} colSpan={4}>Разом:</td>
            <td style={num}>{fmt(mt.old)}</td>
            <td style={cell}></td>
            <td style={num}>{fmt(mt.neu)}</td>
            <td style={num}>{mt.diff < 0 ? '−' : ''}{fmt(Math.abs(mt.diff))}</td>
            <td style={num}>{fmt(mt.income)}</td>
            <td style={num}>{fmt(mt.expense)}</td>
          </tr>
        </tbody>
      </table>
      </div>
        );
      })}

      <div style={{ marginTop: '2rem', fontSize: '10pt' }}>
        Відповідальний: ________________________&nbsp;&nbsp;&nbsp;________________________&nbsp;&nbsp;&nbsp;________________________
        <div style={{ color: '#555', fontSize: '8pt', marginLeft: '7.5rem' }}>
          (посада)&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;(підпис)&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;(розшифровка підпису)
        </div>
      </div>
    </div>
  );
}
